import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  LifeBuoy, CheckCircle2, ArrowLeft, Phone, Mail, Cpu, Monitor, Wifi,
  Lock, AppWindow, HelpCircle, Mail as MailIcon, Phone as PhoneIcon,
} from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

const CATEGORIES = [
  { value: "hardware", label: "Hardware", icon: Cpu },
  { value: "software", label: "Software", icon: Monitor },
  { value: "account_access", label: "Account access", icon: Lock },
  { value: "network_wifi", label: "Network / Wi-Fi", icon: Wifi },
  { value: "email", label: "Email", icon: MailIcon },
  { value: "phone", label: "Phone / mobile", icon: PhoneIcon },
  { value: "applications_systems", label: "Applications / systems", icon: AppWindow },
  { value: "other", label: "Other", icon: HelpCircle },
];

const PRIORITIES = [
  { value: "low", label: "Low — when you can" },
  { value: "medium", label: "Medium — soon" },
  { value: "high", label: "High — today if possible" },
  { value: "critical", label: "Critical — blocking my work" },
];

export default function PublicHelp() {
  const [form, setForm] = useState({
    reporter_name: "",
    reporter_email: "",
    reporter_phone: "",
    organization: "",
    title: "",
    description: "",
    category: "other",
    priority: "medium",
    affected_device: "",
    honeypot: "", // hidden anti-spam field
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.reporter_name.trim() || !form.reporter_email.trim() || !form.title.trim()) {
      setError("Please add your name, email, and a short title for the issue.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/tickets`, form);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.display_id) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-start justify-center px-4 py-10">
        <div className="max-w-xl w-full bg-white rounded-2xl border border-[#E5E5E0] shadow-sm p-8 text-center" data-testid="help-success">
          <div className="w-14 h-14 rounded-2xl bg-[#E8ECE7] text-[#166534] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="hw-eyebrow text-[10px] mb-2">Submitted</div>
          <h1 className="font-display text-2xl font-semibold text-[#0F172A] mb-2">We got your request</h1>
          <p className="text-sm text-[#334155] mb-4">
            Thanks for reaching out, <strong>{form.reporter_name.split(" ")[0]}</strong>! Our IT team
            will jump in and you'll receive an email update at <strong>{form.reporter_email}</strong>.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E0E7FF] text-[#1E3A8A] font-mono text-sm font-semibold tracking-wider mb-6" data-testid="help-reference">
            Reference: {result.display_id}
          </div>
          <p className="text-xs text-[#1E3A8A] mb-6">
            Save this reference number in case you need to follow up by phone or email.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => {
                setResult(null);
                setForm((f) => ({ ...f, title: "", description: "", affected_device: "" }));
              }}
              data-testid="help-another"
              className="px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-sm text-[#334155] hover:bg-[#F9F8F6]"
            >
              Submit another
            </button>
            <Link
              to="/"
              className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
            >
              Back to HarborWell
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Brand bar */}
      <header className="bg-white border-b border-[#E5E5E0]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <HarborWellFormHeader
            eyebrow="Connect"
            size="md"
            testId="help-brand-header"
            rightSlot={
              <Link to="/login" className="text-xs text-[#1E3A8A] font-medium inline-flex items-center gap-1" data-testid="help-staff-login">
                Staff sign-in <ArrowLeft className="w-3 h-3 rotate-180" />
              </Link>
            }
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#E0E7FF] text-[#1E3A8A] flex items-center justify-center mx-auto mb-4">
            <LifeBuoy className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="hw-eyebrow text-[10px] mb-2">Need help?</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
            HarborWell Tech Support
          </h1>
          <p className="text-sm text-[#334155] mt-2 max-w-xl mx-auto">
            Volunteers, partners, and community members — log an IT issue here. Our team responds within one business day.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5E5E0] shadow-sm p-6 space-y-5" data-testid="public-help-form">
          {/* Honeypot — hidden from real users */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.honeypot}
            onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          <fieldset>
            <legend className="hw-eyebrow text-[10px] mb-3">Who you are</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Your name *</label>
                <input
                  required
                  value={form.reporter_name}
                  onChange={(e) => setForm({ ...form, reporter_name: e.target.value })}
                  data-testid="public-name"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Email *</label>
                <input
                  required
                  type="email"
                  value={form.reporter_email}
                  onChange={(e) => setForm({ ...form, reporter_email: e.target.value })}
                  data-testid="public-email"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Phone <span className="normal-case text-[#1E3A8A] font-normal">(optional)</span></label>
                <input
                  type="tel"
                  value={form.reporter_phone}
                  onChange={(e) => setForm({ ...form, reporter_phone: e.target.value })}
                  data-testid="public-phone"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Organization <span className="normal-case text-[#1E3A8A] font-normal">(optional)</span></label>
                <input
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  data-testid="public-org"
                  placeholder="e.g., outreach volunteer, partner agency"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                />
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend className="hw-eyebrow text-[10px] mb-3">What you need help with</legend>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Issue title *</label>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="public-title"
                  placeholder="e.g., Can't sign into the volunteer iPad"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Tell us more</label>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="public-description"
                  placeholder="What were you trying to do? What happened instead? Any error messages?"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="public-category"
                    className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                  >
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">How urgent?</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    data-testid="public-priority"
                    className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                  >
                    {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Device or system <span className="normal-case text-[#1E3A8A] font-normal">(optional)</span></label>
                <input
                  value={form.affected_device}
                  onChange={(e) => setForm({ ...form, affected_device: e.target.value })}
                  data-testid="public-device"
                  placeholder="e.g., HarborWell volunteer iPad #3, my personal Android phone, Chromebook"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                />
              </div>
            </div>
          </fieldset>

          {error && (
            <div className="rounded-xl bg-[#FEE2E2] text-[#991B1B] p-3 text-sm" data-testid="public-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="public-submit"
            className="w-full py-3 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit ticket"}
          </button>

          <p className="text-xs text-[#1E3A8A] text-center">
            We'll only use your contact info to follow up on this request.
          </p>
        </form>

        <div className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
          <a href="tel:" className="flex items-center gap-3 bg-white rounded-2xl border border-[#E5E5E0] p-4 hover:bg-[#F9F8F6]">
            <Phone className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
            <div>
              <div className="text-xs text-[#1E3A8A] uppercase tracking-wider">Need it urgent?</div>
              <div className="font-medium text-[#0F172A]">Call the HarborWell hotline</div>
            </div>
          </a>
          <a href="mailto:harborwellconnect@mail.harborwell.care" className="flex items-center gap-3 bg-white rounded-2xl border border-[#E5E5E0] p-4 hover:bg-[#F9F8F6]">
            <Mail className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
            <div>
              <div className="text-xs text-[#1E3A8A] uppercase tracking-wider">Prefer email?</div>
              <div className="font-medium text-[#0F172A]">harborwellconnect@mail.harborwell.care</div>
            </div>
          </a>
        </div>
      </main>
    </div>
  );
}
